import { type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";
import { forwardRef } from "react";
import { buttonVariants } from "./styles";
import { Spinner } from "../spinner";

export interface ButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  hoverLabel?: string;
  loading?: boolean;
  // Allow any data-* or aria-* attributes
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | number | boolean | undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size, hoverLabel, loading, children, ...props }, ref) => {
    if (hoverLabel) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                ref={ref}
                data-slot="button"
                className={cn(
                  buttonVariants({ variant, size, className }),
                  props.disabled && "disabled:pointer-events-auto"
                )}
                {...props}
                disabled={loading || props.disabled}
              >
                {!!loading && <Spinner size="xs" color={variant === "default" ? "white" : "secondary"} />}
                {children}
              </button>
            </TooltipTrigger>
            <TooltipContent>{hoverLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <button
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
        disabled={loading || props.disabled}
      >
        {!!loading && <Spinner size="xs" color={variant === "default" ? "white" : "secondary"} />}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";
